/**
 * Sezione "Notifiche vulnerabilita" nel dettaglio prodotto (Slice C — Impatto + notifiche manuali).
 *
 * Per la release selezionata mostra:
 *  - i finding (dalla triage del progetto DT della release) con badge "N clienti impattati"
 *  - azioni "Notifica cliente..." (un cliente alla volta) e "Notifica tutti gli impattati"
 *  - storico invii (ledger): origin manuale/automatico, esito, destinatari, chi e quando
 *
 * Le azioni di invio sono visibili solo con permesso PORTFOLIO_MANAGEMENT (come
 * OwnershipSection/DeploymentsSection). Tutte le notifiche sono inviate manualmente
 * dall'analyst — nessun invio automatico.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '../../auth/index.js';
import { fetchTriage } from '../triage/api.js';
import {
  useReleases,
  useComponents,
  useImpact,
  useNotifications,
  useNotifyCustomer,
  useNotifyAllImpacted,
} from './hooks.js';
import type { CustomerVulnNotification, ImpactedCustomer } from './types.js';
import type { TriageItem } from '../triage/types.js';
import { clientiDaNotificarePerFinding, contaTotaleDaNotificare } from './pendingNotifications.js';
import styles from './Products.module.css';

// ---------------------------------------------------------------------------
// Helper: etichette stato/origin ledger
// ---------------------------------------------------------------------------

const ETICHETTE_STATO: Record<CustomerVulnNotification['status'], string> = {
  sent: 'Inviata',
  failed: 'Fallita',
};

const ETICHETTE_ORIGINE: Record<CustomerVulnNotification['origin'], string> = {
  manual: 'Manuale',
  auto: 'Automatica',
};

// ---------------------------------------------------------------------------
// Sotto-componente: selettore release
// ---------------------------------------------------------------------------

function SelettoreRelease({
  productId,
  version,
  onChange,
}: {
  productId: string;
  version: string;
  onChange: (version: string) => void;
}) {
  const { data: releases = [], isLoading } = useReleases(productId);
  // Solo le release con un progetto DT hanno finding da notificare.
  const releasesConProgetto = releases.filter((r) => r.dtProjectUuid);

  return (
    <div className={styles.formRow} style={{ marginBottom: '1rem' }}>
      <div className={styles.formGroup} style={{ maxWidth: '260px' }}>
        <label className={styles.formLabel} htmlFor="notifiche-release">
          Release
        </label>
        <select
          id="notifiche-release"
          className={styles.formSelect}
          value={version}
          onChange={(e) => onChange(e.target.value)}
          disabled={isLoading}
          data-testid="select-notifiche-release"
        >
          <option value="">Seleziona release...</option>
          {releasesConProgetto.map((r) => (
            <option key={r.version} value={r.version}>
              {r.version}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componente: dialog "Notifica cliente..."
// ---------------------------------------------------------------------------

function NotificaClienteDialog({
  clienti,
  onConferma,
  onAnnulla,
  isPending,
}: {
  clienti: ImpactedCustomer[];
  onConferma: (customerId: number) => void;
  onAnnulla: () => void;
  isPending: boolean;
}) {
  const [customerId, setCustomerId] = useState<number | ''>('');

  return (
    <div className={styles.formCard} data-testid="dialog-notifica-cliente">
      <p className={styles.formTitle}>Notifica cliente</p>
      <div className={styles.formRow}>
        <div className={styles.formGroup} style={{ flex: 2 }}>
          <label className={styles.formLabel} htmlFor="notifica-cliente-select">
            Cliente impattato
          </label>
          <select
            id="notifica-cliente-select"
            className={styles.formSelect}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value === '' ? '' : Number(e.target.value))}
            data-testid="select-notifica-cliente"
          >
            <option value="">Seleziona cliente...</option>
            {clienti.map((c) => (
              <option key={c.customerId} value={c.customerId}>
                {c.customerName}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={isPending || customerId === ''}
          onClick={() => customerId !== '' && onConferma(customerId)}
          data-testid="btn-conferma-notifica-cliente"
        >
          {isPending ? 'Invio...' : 'Invia notifica'}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={isPending}
          onClick={onAnnulla}
          data-testid="btn-annulla-notifica-cliente"
        >
          Annulla
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componente: storico invii (ledger)
// ---------------------------------------------------------------------------

function StoricoInvii({ notifiche }: { notifiche: CustomerVulnNotification[] }) {
  if (notifiche.length === 0) {
    return (
      <div className={styles.emptyState} data-testid="storico-invii-vuoto">
        Nessun invio registrato per questa release.
      </div>
    );
  }

  return (
    <table className={styles.table} data-testid="tabella-storico-invii">
      <thead>
        <tr>
          <th>Vulnerabilita</th>
          <th>Cliente</th>
          <th>Origine</th>
          <th>Esito</th>
          <th>Destinatari</th>
          <th>Inviata da</th>
          <th>Data</th>
        </tr>
      </thead>
      <tbody>
        {notifiche.map((n) => (
          <tr key={n.id} data-testid={`storico-row-${n.id}`}>
            <td>{n.vulnId}</td>
            <td>{n.customerId}</td>
            <td>
              <span className={styles.badge}>{ETICHETTE_ORIGINE[n.origin]}</span>
            </td>
            <td>
              {n.status === 'sent' ? (
                <span className={styles.badge} data-testid={`storico-status-${n.id}`}>
                  {ETICHETTE_STATO[n.status]}
                </span>
              ) : (
                <span
                  className={styles.inlineMutError}
                  data-testid={`storico-status-${n.id}`}
                  title={n.error}
                >
                  {ETICHETTE_STATO[n.status]}
                </span>
              )}
            </td>
            <td>{n.recipients?.join(', ') ?? '-'}</td>
            <td>{n.sentBy ?? '-'}</td>
            <td>{n.sentAt ?? '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componente: riga finding con azioni
// ---------------------------------------------------------------------------

function FindingRow({
  productId,
  version,
  finding,
  clientiImpattati,
  clientiDaNotificare,
  puoGestire,
}: {
  productId: string;
  version: string;
  finding: TriageItem;
  clientiImpattati: ImpactedCustomer[];
  clientiDaNotificare: ImpactedCustomer[];
  puoGestire: boolean;
}) {
  const [dialogApertoCliente, setDialogApertoCliente] = useState(false);

  const notifyCustomer = useNotifyCustomer(productId, version);
  const notifyAll = useNotifyAllImpacted(productId, version);

  const vulnId = finding.cveId;
  const severity = finding.severity ?? 'UNASSIGNED';

  function messaggioErrore(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback;
  }

  return (
    <div className={styles.formCard} data-testid={`finding-row-${vulnId}`}>
      <div className={styles.formRow} style={{ alignItems: 'center' }}>
        <div className={styles.formGroup} style={{ flex: 2 }}>
          <span className={styles.formTitle} style={{ margin: 0 }}>
            {vulnId}
          </span>
          <span style={{ fontSize: '0.85em', color: '#666' }}>{finding.componentName}</span>
        </div>
        <div className={styles.formGroup}>
          <span className={styles.badge}>{severity}</span>
        </div>
        <div className={styles.formGroup}>
          <span className={styles.badge} data-testid={`badge-clienti-impattati-${vulnId}`}>
            {clientiImpattati.length} client
            {clientiImpattati.length === 1 ? 'e' : 'i'} impattat
            {clientiImpattati.length === 1 ? 'o' : 'i'}
          </span>
        </div>
        <div className={styles.formGroup}>
          <span className={styles.badge} data-testid={`badge-da-notificare-${vulnId}`}>
            {clientiDaNotificare.length} da notificare
          </span>
        </div>
      </div>

      {puoGestire && (
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => setDialogApertoCliente((open) => !open)}
            disabled={clientiImpattati.length === 0}
            data-testid={`btn-notifica-cliente-${vulnId}`}
          >
            Notifica cliente...
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={clientiImpattati.length === 0 || notifyAll.isPending}
            onClick={() => notifyAll.mutate({ vulnId, input: { severity } })}
            data-testid={`btn-notifica-tutti-${vulnId}`}
          >
            {notifyAll.isPending ? 'Invio...' : 'Notifica tutti gli impattati'}
          </button>
        </div>
      )}

      {puoGestire && notifyCustomer.isError && (
        <span className={styles.inlineMutError} data-testid={`errore-notifica-cliente-${vulnId}`}>
          {messaggioErrore(notifyCustomer.error, "Errore durante l'invio della notifica.")}
        </span>
      )}

      {puoGestire && notifyAll.isError && (
        <span className={styles.inlineMutError} data-testid={`errore-notifica-tutti-${vulnId}`}>
          {messaggioErrore(notifyAll.error, "Errore durante l'invio massivo.")}
        </span>
      )}

      {puoGestire && notifyAll.isSuccess && (
        <span className={styles.successMsg} data-testid={`successo-notifica-tutti-${vulnId}`}>
          Notifica inviata a {notifyAll.data.results.filter((r) => r.ok).length} di{' '}
          {notifyAll.data.results.length} client
          {notifyAll.data.results.length === 1 ? 'e' : 'i'}.
        </span>
      )}

      {puoGestire && dialogApertoCliente && (
        <NotificaClienteDialog
          clienti={clientiImpattati}
          isPending={notifyCustomer.isPending}
          onAnnulla={() => {
            setDialogApertoCliente(false);
            notifyCustomer.reset();
          }}
          onConferma={(customerId) => {
            notifyCustomer.mutate(
              { vulnId, input: { customerId, severity } },
              {
                onSuccess: () => setDialogApertoCliente(false),
              },
            );
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principale: NotificationsSection
// ---------------------------------------------------------------------------

/** Sezione "Notifiche vulnerabilita" del dettaglio prodotto. */
export function NotificationsSection({ productId }: { productId: string }) {
  const { can } = usePermissions();
  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  const { data: releases = [] } = useReleases(productId);
  const releasesConProgetto = releases.filter((r) => r.dtProjectUuid);

  const [version, setVersion] = useState('');
  // Se nessuna release e' selezionata, usa la prima release con progetto DT.
  const releaseAttiva = version || releasesConProgetto[0]?.version || '';

  // I finding vivono sulle FOGLIE (bundle) della release: si recuperano per-bundle e si fondono
  // (dedup per cveId). La Release è una Collection Project senza finding propri.
  const { data: bundles = [] } = useComponents(productId, releaseAttiva);
  const bundleUuids = bundles
    .map((b) => b.dtProjectUuid)
    .filter((u): u is string => typeof u === 'string');

  const {
    data: findings,
    isLoading: findingsLoading,
    isError: findingsError,
    error: findingsErrorObj,
  } = useQuery({
    queryKey: ['triage-notifiche', releaseAttiva, bundleUuids],
    queryFn: async () => {
      const perBundle = await Promise.all(bundleUuids.map((u) => fetchTriage(u)));
      const perCve = new Map<string, TriageItem>();
      for (const pagina of perBundle) {
        for (const item of pagina.items) if (!perCve.has(item.cveId)) perCve.set(item.cveId, item);
      }
      return [...perCve.values()];
    },
    enabled: bundleUuids.length > 0,
  });

  const {
    data: impact,
    isLoading: impactLoading,
    isError: impactError,
    error: impactErrorObj,
  } = useImpact(productId, releaseAttiva);

  const { data: notifiche = [] } = useNotifications(productId, releaseAttiva);

  const [soloDaNotificare, setSoloDaNotificare] = useState(false);

  const clientiImpattati = impact?.customers ?? [];
  const vulnIds = (findings ?? []).map((f) => f.cveId);
  const daNotificarePerFinding = clientiDaNotificarePerFinding(
    vulnIds,
    clientiImpattati,
    notifiche,
  );
  const totaleDaNotificare = contaTotaleDaNotificare(daNotificarePerFinding);

  const findingsDaMostrare = soloDaNotificare
    ? (findings ?? []).filter((f) => (daNotificarePerFinding.get(f.cveId)?.length ?? 0) > 0)
    : (findings ?? []);

  return (
    <div data-testid="sezione-notifiche">
      <SelettoreRelease productId={productId} version={releaseAttiva} onChange={setVersion} />

      {!releaseAttiva && (
        <div className={styles.emptyState} data-testid="notifiche-nessuna-release">
          Nessuna release con progetto Dependency-Track disponibile per le notifiche.
        </div>
      )}

      {releaseAttiva && (
        <>
          <div className={styles.formRow} style={{ marginBottom: '1rem', alignItems: 'center' }}>
            <span className={styles.badge} data-testid="badge-totale-da-notificare">
              {totaleDaNotificare} da notificare
            </span>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                marginLeft: '1rem',
              }}
            >
              <input
                type="checkbox"
                checked={soloDaNotificare}
                onChange={(e) => setSoloDaNotificare(e.target.checked)}
                data-testid="toggle-solo-da-notificare"
              />
              Solo da notificare
            </label>
          </div>

          {(findingsLoading || impactLoading) && (
            <p className={styles.loadingMsg} data-testid="loading-notifiche">
              Caricamento dati notifiche...
            </p>
          )}

          {findingsError && (
            <p className={styles.errorMsg} data-testid="errore-finding-notifiche">
              {findingsErrorObj instanceof Error
                ? findingsErrorObj.message
                : 'Errore nel caricamento dei finding'}
            </p>
          )}

          {impactError && (
            <p className={styles.errorMsg} data-testid="errore-impatto-notifiche">
              {impactErrorObj instanceof Error
                ? impactErrorObj.message
                : "Errore nel caricamento dell'impatto"}
            </p>
          )}

          {!findingsLoading &&
            !impactLoading &&
            findings !== undefined &&
            findings.length === 0 && (
              <div className={styles.emptyState} data-testid="notifiche-nessun-finding">
                Nessuna vulnerabilita per questa release.
              </div>
            )}

          {!findingsLoading &&
            !impactLoading &&
            findings !== undefined &&
            findings.length > 0 &&
            findingsDaMostrare.length === 0 && (
              <div className={styles.emptyState} data-testid="notifiche-nessuna-da-notificare">
                Nessuna notifica da inviare per questa release.
              </div>
            )}

          {!findingsLoading &&
            !impactLoading &&
            findings !== undefined &&
            findingsDaMostrare.length > 0 && (
              <div data-testid="lista-finding-notifiche">
                {findingsDaMostrare.map((finding) => (
                  <FindingRow
                    key={`${finding.vulnerabilityUuid}-${finding.componentUuid}`}
                    productId={productId}
                    version={releaseAttiva}
                    finding={finding}
                    clientiImpattati={clientiImpattati}
                    clientiDaNotificare={daNotificarePerFinding.get(finding.cveId) ?? []}
                    puoGestire={puoGestire}
                  />
                ))}
              </div>
            )}

          <h3 className={styles.sectionTitle}>Storico invii</h3>
          <StoricoInvii notifiche={notifiche} />
        </>
      )}
    </div>
  );
}
